import { specialCollectionsApi } from "@api/specialCollections/specialCollections";
import { withCancel } from "@api/utils";
import { Error } from "@components/Error";
import { Loader } from "@components/Loader";
import { RJSForm } from "@forms/rjsf";
import { goTo } from "@history";
import { BackOfficeRoutes } from "@routes/urls";
import { collegeProgramApi } from "@api/colleges/colleges";
import _get from "lodash/get";
import PropTypes from "prop-types";
import React, { Component } from "react";
import { schema } from "./schema";
import { uiSchema } from "./uiSchema";

export class SpecialCollectionEditor extends Component {
  constructor(props) {
    super(props);

    this.state = {
      data: {},
      isLoading: this.userIsEditing,
      error: {},
      colleges: [],
      programs: [],
      programsByCollege: {},
      vocabulariesLoading: true,
    };
  }
  componentDidMount() {
    const { match } = this.props;
    this.fetchVocabularies();
    if (match.params.specialCollectionPid) {
      this.fetch(match.params.specialCollectionPid);
    }
  }

  componentWillUnmount() {
    this.cancellableFetch && this.cancellableFetch.cancel();
    this.cancelVocabsFetch && this.cancelVocabsFetch.cancel();
  }

  get userIsEditing() {
    return !!this.props.match.params.specialCollectionPid;
  }

  fetchVocabularies = async () => {
    try {
      this.cancelVocabsFetch = withCancel(
        collegeProgramApi.listCollegesWithPrograms()
      );

      const response = await this.cancelVocabsFetch.promise;
      const collegesData = response.data.data;

      // Create mapping of college_id to programs
      const programsByCollege = {};
      collegesData.forEach((college) => {
        programsByCollege[String(college.id)] = college.programs || [];
      });

      this.setState({
        colleges: collegesData,
        programs: collegesData.flatMap((c) => c.programs || []),
        programsByCollege: programsByCollege,
        vocabulariesLoading: false,
      });
    } catch (error) {
      if (error !== "UNMOUNTED") {
        this.setState({ vocabulariesLoading: false });
      }
    }
  };

  fetch = async (specialCollectionPid) => {
    this.cancellableFetch = withCancel(
      specialCollectionsApi.get(specialCollectionPid)
    );
    try {
      const response = await this.cancellableFetch.promise;
      this.setState({ data: response.data, isLoading: false, error: {} });
    } catch (error) {
      if (error !== "UNMOUNTED") {
        this.setState({ isLoading: false, error: error });
      }
    }
  };

  submitAction = async (formData) => {
    const {
      match: {
        params: { specialCollectionPid },
      },
    } = this.props;

    if (!this.userIsEditing) {
      if (!formData.created_by) {
        formData.created_by = {
          type: "user",
          value: "current_user",
        };
      }

      formData.$schema =
        "https://localhost:5000/schemas/special_collections/special-collection-v1.0.0.json";
    }

    try {
      const result = this.userIsEditing
        ? await specialCollectionsApi.update(specialCollectionPid, formData)
        : await specialCollectionsApi.create(formData);

      return result;
    } catch (error) {
      console.error("API call failed:", error);
      throw error;
    }
  };

  successCallback = async (response) => {
    const newSpecialCollectionPid = response.data.metadata.pid;
    goTo(BackOfficeRoutes.specialCollectionDetailsFor(newSpecialCollectionPid));
  };

  render() {
    const {
      match: {
        params: { specialCollectionPid },
      },
    } = this.props;
    const {
      isLoading,
      error,
      data,
      colleges,
      programs,
      vocabulariesLoading,
      programsByCollege,
    } = this.state;

    if (this.userIsEditing) {
      const formTitle = `Thesis and Dissertation - Edit #${specialCollectionPid}`;

      let formData = {};
      if (data.metadata) {
        formData = { ...data.metadata };

        if (formData.academic_info?.program && programs.length > 0) {
          const programId = formData.academic_info.program;
          const program = programs.find((p) => p.id === programId);
          if (program) {
            formData.academic_info.program = program.key;
          }
        }
      }

      return (
        <Loader isLoading={isLoading || vocabulariesLoading}>
          <Error error={error}>
            <RJSForm
              schema={schema(colleges, programs)}
              uiSchema={uiSchema(formTitle, colleges, programsByCollege)}
              formData={formData}
              submitAction={this.submitAction}
              successCallback={this.successCallback}
              successMessage="The special collection was successfully updated."
            />
          </Error>
        </Loader>
      );
    } else {
      const formTitle = "Thesis and Dissertation - Create";
      const prefilledFormData = _get(this.props, "location.state.formData", {});
      return (
        <Loader isLoading={vocabulariesLoading}>
          <RJSForm
            schema={schema(colleges, programs)}
            uiSchema={uiSchema(formTitle, colleges, programsByCollege)}
            formData={prefilledFormData}
            submitAction={this.submitAction}
            successCallback={this.successCallback}
            successMessage="The special collection was successfully created."
          />
        </Loader>
      );
    }
  }
}

SpecialCollectionEditor.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      specialCollectionPid: PropTypes.string,
    }),
  }).isRequired,
  location: PropTypes.shape({
    state: PropTypes.shape({
      formData: PropTypes.object,
    }),
  }),
};

SpecialCollectionEditor.defaultProps = {
  location: {
    state: null,
  },
};
